import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {issuePath} from '@github-ui/paths'
import {createIssueFieldValueMutation as createIssueFieldValueQueryId} from '@github-ui/server-persisted-queries'
import type {CreateIssueFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/createIssueFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {Environment} from 'relay-runtime'

import {
  type IssueFieldValueMutationInput,
  type IssueFieldValueMutationRoute,
  normalizeIssueFieldValue,
  type OptimisticIssueFieldValue,
  upsertIssueFieldValue,
} from './helpers'
import {applyOptimisticIssueFieldValueToRelay, commitIssueFieldValueToRelay} from './issue-field-value-relay-bridge'

const CREATE_ERROR = 'Create issue field value failed'
type CreateOptions = {
  input: IssueFieldValueMutationInput
  optimisticFieldValue?: OptimisticIssueFieldValue
  onCompleted?: () => void
  onError?: (error?: Error) => void
}
type CreateResponse = {data?: CreateIssueFieldValueMutationMutation | null; errors?: readonly unknown[] | null}

export async function executeNativeCreateIssueFieldValueMutation(
  route: IssueFieldValueMutationRoute,
  environment: Environment,
  options: CreateOptions,
) {
  const {input, optimisticFieldValue, onCompleted} = options
  const optimisticRelayUpdate =
    optimisticFieldValue && applyOptimisticIssueFieldValueToRelay(environment, input, optimisticFieldValue)

  try {
    const query = new URLSearchParams({queryId: createIssueFieldValueQueryId})
    const path = `${issuePath({owner: route.owner, repo: route.repository, issueNumber: route.number})}/field_value.json`
    const response = await reactFetchJSON(`${path}?${query.toString()}`, {
      method: 'POST',
      body: {issueField: input.issueField},
    })
    if (!response.ok) throw new Error(`${CREATE_ERROR} (${response.status})`)

    let json: CreateResponse
    try {
      json = (await response.json()) as CreateResponse
    } catch {
      throw new Error(`${CREATE_ERROR}: response was invalid`)
    }
    if (json.errors?.length) {
      const message = json.errors
        .flatMap(error =>
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? [error.message]
            : [],
        )
        .join(', ')
      throw new Error(message || `${CREATE_ERROR}: GraphQL errors were returned`)
    }
    if (!json.data) throw new Error(`${CREATE_ERROR}: response data was missing`)

    const payload = json.data.createIssueFieldValue
    if (!payload) throw new Error(`${CREATE_ERROR}: payload was missing`)
    if (!payload.issue) throw new Error(`${CREATE_ERROR}: issue was missing`)
    if (payload.issue.id !== input.issueId) throw new Error(`${CREATE_ERROR}: issue did not match`)
    if (!payload.issueFieldValue) throw new Error(`${CREATE_ERROR}: issue field value was missing`)

    const confirmed = optimisticFieldValue
      ? normalizeIssueFieldValue(payload.issueFieldValue, optimisticFieldValue)
      : undefined
    if (
      optimisticFieldValue &&
      (!confirmed || payload.issueFieldValue.__typename !== optimisticFieldValue.__typename)
    ) {
      throw new Error(`${CREATE_ERROR}: issue field value was invalid`)
    }
    commitIssueFieldValueToRelay(environment, input.issueId, input.issueField.fieldId, payload.issueFieldValue)
    if (confirmed) publishOptimisticFields().completed(upsertIssueFieldValue(input.issueId, confirmed))
    onCompleted?.()
  } finally {
    optimisticRelayUpdate?.dispose()
  }
}
